import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OtpInput from '../components/common/OtpInput';
import RoleSelectModal from '../components/common/RoleSelectModal';
import AuthShell from '../components/layout/AuthShell';
import {
  UI, hL, lead, input, fieldLabel, iconbtn, btnLg, btnGhost, textbtn, lnk,
  spin, alertBad, mono, num,
} from '../styles/tl5';

import {
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useVerifyCredentialsMutation,
  useVerifyCredentialsOtpMutation,
  useVerifyDoctorOtpMutation,
} from '../store/authApi';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';

const Icon = ({ d, size = 18 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: size, height: size, minWidth: size, display: 'block' }}
  >
    {d}
  </Box>
);
const EyeIcon = <Icon d={<><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>} />;
const EyeOffIcon = <Icon d={<><path d="M3 3l18 18" /><path d="M10.6 6.2A9.9 9.9 0 0 1 12 5c6.4 0 10 7 10 7a18 18 0 0 1-3.2 4M6.2 7.6A17.7 17.7 0 0 0 2 12s3.6 7 10 7a9.6 9.6 0 0 0 4-.85" /></>} />;
// ChangePasswordModal lives on the portal pages now — see
// MedicalDashboard / LabDashboard. We don't pop it on /login.

// Two-step login flow with two entry paths:
//
//   Mode A (default) — Client ID / UHID + password → OTP → home
//   Mode B           — Mobile number → OTP → home
//
// Both paths converge on the same OTP screen. In Mode A the OTP goes
// to the phone number we have on file for the user; in Mode B it goes
// to whatever the user typed. Until the API is wired, Mode A uses a
// stub fallback phone for the OTP screen's display copy.
//
// API hook-up plan (when endpoints land):
//   onCredsSubmit  → /api/auth/login        { client_id, password }
//                       returns: { masked_phone }       (stash for OTP screen)
//   onPhoneSubmit  → /api/auth/request-otp  { phone }
//                       returns: 200 (or 404 → redirect to signup)
//   onOtpSubmit    → /api/auth/verify-otp   { otp, ... }
//                       returns: { token, user }        (setSession, navigate)

const STUB_FALLBACK_PHONE = '98******10'; // shown on OTP screen until API supplies the real masked phone

// Picks the destination route after a successful credentials login,
// based on the user's role. Each portal pops its own change-password
// modal when must_change_password is true, so we don't need to do
// any of that here — just send them home to their portal.
const portalForUser = (user) => {
  switch (user?.user_type) {
    case 'patient':      return user?.uhid ? ROUTES.DASHBOARD : ROUTES.HOME;
    case 'partner':      return ROUTES.MED_DASHBOARD;
    case 'doctor':       return ROUTES.MED_DASHBOARD;   // doctor session lands on same shell
    case 'hospital':     return ROUTES.LAB_DASHBOARD;
    case 'organization': return ROUTES.CORP_DASHBOARD;
    default:             return ROUTES.HOME;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const { showToast } = useUI();

  const [step, setStep] = useState('credentials'); // 'credentials' | 'phone' | 'otp'
  // `mode` records which path led the user to the OTP screen so the
  // back button + the OTP-screen copy stay consistent, AND so the
  // OTP submit dispatches against the correct endpoint:
  //   'credentials' — UHID or client_id "Continue as Client"
  //   'phone'       — mobile-only flow (Mode B)
  //   'doctor'      — "Login as Doctor" fork (verifies via the
  //                   /api/auth/doctor/verify-otp endpoint and mints
  //                   a JWT carrying role='doctor').
  const [mode, setMode] = useState('credentials'); // 'credentials' | 'phone' | 'doctor'

  const [clientId, setClientId] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const [requestOtp,           { isLoading: sending }]    = useRequestOtpMutation();
  const [verifyOtp,            { isLoading: verifying }]  = useVerifyOtpMutation();
  const [verifyCredentials,    { isLoading: signingIn }]  = useVerifyCredentialsMutation();
  const [verifyCredentialsOtp, { isLoading: signingInOtp }] = useVerifyCredentialsOtpMutation();
  const [verifyDoctorOtp,      { isLoading: verifyingDoctor }] = useVerifyDoctorOtpMutation();

  // Role-select modal — shown after a successful credentials verify
  // ONLY when the identifier matched against client_id (centre user).
  // Patients (UHID match) skip the modal and go straight to OTP.
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  // Friendly centre name (e.g. "Begumpet Diagnostics") — surfaced in
  // the modal so the doctor sees which centre they're logging into.
  const [centreName, setCentreName] = useState('');
  // Doctor-mode phone — captured by the modal's step-2 input, kept
  // here so the OTP screen can pass it through to /verify-otp.
  const [doctorPhone, setDoctorPhone] = useState('');
  const [doctorName,  setDoctorName]  = useState('');

  // Masked phone returned by /verify-credentials. Shown on the OTP
  // screen (e.g. "+91 98******10") so the partner sees where the
  // OTP went without exposing the full number.
  const [maskedPhone, setMaskedPhone] = useState('');

  // 10-digit number with country code prefix — same shape Signup uses.
  const fullPhone = `+91${phone}`;

  // Phone shown on the OTP screen — comes from the user's profile in
  // the credentials flow (stubbed here), or the entered number in the
  // mobile-only flow.
  const otpDisplayPhone = mode === 'phone' ? phone : STUB_FALLBACK_PHONE;

  // ── Step 1A: Client ID / UHID + password ──
  // Step 1 of two. Calls /api/auth/verify-credentials. The backend
  // matches the identifier (UHID for patients, client_id for doctors
  // / lab partners), checks the password, and dispatches a 6-digit
  // OTP to the user's registered phone. We don't get a session here
  // — only `requiresOtp + masked_phone`. The OTP step (handled in
  // onOtpSubmit when mode='credentials') completes the login.
  const onCredsSubmit = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!clientId.trim()) return setErrMsg('Enter your Client ID or UHID');
    if (!password)        return setErrMsg('Enter your password');

    try {
      const res = await verifyCredentials({
        identifier: clientId.trim(),
        password,
      }).unwrap();
      setMaskedPhone(res.masked_phone || '');
      setCentreName(res.centre_name || '');
      // Patients (matched on UHID) skip the role-select modal and go
      // straight to the existing OTP screen. Centre users matched on
      // client_id see the modal to choose Client vs Doctor — EXCEPT
      // organisations, which have no referring-doctor concept and
      // route directly into the corporate portal after OTP.
      if (res.identifier_type === 'client_id' && res.user_type !== 'organization') {
        setRoleModalOpen(true);
        return;
      }
      setMode('credentials');
      setStep('otp');
      showToast(`OTP sent to ${res.masked_phone || 'your registered mobile'}`);
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not sign in. Please try again.');
    }
  };

  // ── Role-select modal callbacks ──
  // "Continue as Client" — keep the existing credentials-OTP flow.
  // The OTP has already been dispatched by /verify-credentials above,
  // so we just close the modal and switch to the OTP step.
  const onContinueAsClient = () => {
    setRoleModalOpen(false);
    setMode('credentials');
    setStep('otp');
    showToast(`OTP sent to ${maskedPhone || 'your registered mobile'}`);
  };
  // "Login as Doctor" → after the modal's send-otp succeeds the
  // doctor's phone + masked phone are surfaced here, the modal closes
  // itself, and we switch into 'doctor' OTP mode.
  const onDoctorOtpSent = ({ phone: docPhone, masked_phone, doctor_name }) => {
    setDoctorPhone(docPhone);
    setDoctorName(doctor_name || '');
    setMaskedPhone(masked_phone || '');
    setRoleModalOpen(false);
    setMode('doctor');
    setStep('otp');
    showToast(`OTP sent to Dr. ${doctor_name}'s number`);
  };

  // ── Step 1B: Mobile number → OTP ──
  // Wired to /api/auth/request-otp. If the phone isn't registered the
  // backend returns `{ requiresSignup: true }` — we forward the
  // number to the signup page so the user doesn't have to retype it.
  const onPhoneSubmit = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!/^\d{10}$/.test(phone)) return setErrMsg('Enter a valid 10-digit mobile number');

    try {
      const res = await requestOtp({ phone: fullPhone }).unwrap();
      if (res?.requiresSignup) {
        showToast('No account found — please sign up');
        navigate(`${ROUTES.SIGNUP}?phone=${encodeURIComponent(phone)}`);
        return;
      }
      setMode('phone');
      setStep('otp');
      showToast('OTP sent — check your phone');
    } catch (err) {
      setErrMsg(err?.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  // ── Step 2: OTP verification ──
  // Mode 'phone'       → /api/auth/verify-otp with { phone, otp }, sets session.
  // Mode 'credentials' → still static (UHID/password verify endpoint
  //                       not yet built). Just navigates home for now.
  const onOtpSubmit = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!/^\d{6}$/.test(otp.trim())) return setErrMsg('Enter the 6-digit OTP');

    if (mode === 'phone') {
      try {
        const res = await verifyOtp({ phone: fullPhone, otp: otp.trim() }).unwrap();
        if (res?.requiresSignup) {
          showToast('Please complete signup');
          navigate(`${ROUTES.SIGNUP}?phone=${encodeURIComponent(phone)}`);
          return;
        }
        // OTP-only login doesn't prove UHID/password ownership, so
        // /reports should still ask for UHID confirmation afterwards.
        setSession({ token: res.token, user: res.user, uhidVerified: false });
        showToast(`Welcome back, ${res.user.name}!`);
        const next = new URLSearchParams(location.search).get('next') || ROUTES.HOME;
        navigate(next, { replace: true });
      } catch (err) {
        setErrMsg(err?.data?.message || 'Invalid OTP. Please try again.');
      }
      return;
    }

    // Doctor-mode OTP → /api/auth/doctor/verify-otp. The backend now
    // upserts a User row with user_type='doctor' and mints a regular
    // JWT against it, so the response shape is identical to every
    // other login. AuthContext derives isDoctor from user.user_type;
    // no extra fields to stash.
    if (mode === 'doctor') {
      try {
        const res = await verifyDoctorOtp({
          client_id: clientId.trim(),
          phone: doctorPhone,
          otp: otp.trim(),
        }).unwrap();
        setSession({ token: res.token, user: res.user, uhidVerified: true });
        showToast(`Welcome, ${res.user?.name || 'Doctor'}`);
        navigate(portalForUser(res.user), { replace: true });
      } catch (err) {
        setErrMsg(err?.data?.message || 'Invalid OTP. Please try again.');
      }
      return;
    }

    // Credentials path — Step 2: verify the OTP for the same
    // identifier the user typed earlier. On success we set the JWT
    // session, mark uhidVerified so /reports skips its gate, and
    // navigate the user straight to their role-specific portal.
    // The change-password modal is no longer popped here — each
    // portal page (MedicalDashboard / LabDashboard) auto-pops its
    // own modal when `user.must_change_password` is true, so the
    // user lands on their portal first and then sees the dialog.
    try {
      const res = await verifyCredentialsOtp({
        identifier: clientId.trim(),
        otp: otp.trim(),
      }).unwrap();
      setSession({ token: res.token, user: res.user, uhidVerified: true });
      showToast(`Welcome back, ${res.user.name}`);
      navigate(portalForUser(res.user), { replace: true });
    } catch (err) {
      setErrMsg(err?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  // Headline + subline copy per step. Kept inline so the JSX below
  // stays readable.
  const headline =
    step === 'credentials' ? 'Welcome back' :
    step === 'phone'       ? 'Sign in with mobile' :
                             'Verify your phone';

  const subline =
    step === 'credentials' ? 'Sign in with your Client ID or UHID and password.' :
    step === 'phone'       ? "We'll send a one-time password to your mobile." :
    mode  === 'credentials'
      ? `We've sent a 6-digit OTP to ${maskedPhone || 'your registered mobile'}.`
      : mode === 'doctor'
        ? `We've sent a 6-digit OTP to ${maskedPhone || `Dr. ${doctorName || 'your'}'s number`}.`
        : `We've sent a 6-digit OTP to +91 ${otpDisplayPhone}.`;

  return (
    <AuthShell>
      <Typography component="h1" sx={hL}>{headline}</Typography>
      <Typography sx={{ ...lead, mt: '8px', mb: '26px' }}>{subline}</Typography>

      {errMsg && <Box sx={{ ...alertBad, mb: '20px' }} role="alert">{errMsg}</Box>}

      {/* ── Step 1A: Client ID / UHID + password ── */}
      {step === 'credentials' && (
        <Box component="form" onSubmit={onCredsSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Box>
            <Box component="label" htmlFor="lg-id" sx={fieldLabel}>Client ID / UHID</Box>
            <Box
              component="input"
              id="lg-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="CL-12345 or UH-99887"
              autoComplete="username"
              autoFocus
              disabled={signingIn}
              sx={{ ...input, ...mono, fontSize: 15 }}
            />
          </Box>

          <Box>
            <Box component="label" htmlFor="lg-pw" sx={fieldLabel}>Password</Box>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="input"
                id="lg-pw"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={signingIn}
                sx={{ ...input, pr: '56px' }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Hide password' : 'Show password'}
                sx={{ ...iconbtn, position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
              >
                {showPwd ? EyeOffIcon : EyeIcon}
              </Box>
            </Box>
          </Box>

          <Box component="button" type="submit" disabled={signingIn} sx={{ ...btnLg, width: '100%' }}>
            {signingIn && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
            {signingIn ? 'Checking…' : 'Continue'}
          </Box>

          {/* "or" rule, then the mobile-only route in */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', my: '2px' }}>
            {/* `height: 1` in sx means 100%, not 1px — MUI treats numbers
                0–1 as fractions for width/height, so the rules vanished. */}
            <Box sx={{ flex: 1, height: '1px', background: 'var(--tl-line)' }} />
            <Box component="span" sx={{ fontFamily: UI, fontSize: 13.5, color: 'var(--tl-muted)' }}>or</Box>
            <Box sx={{ flex: 1, height: '1px', background: 'var(--tl-line)' }} />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => { setStep('phone'); setErrMsg(''); }}
            sx={{ ...btnGhost, width: '100%' }}
          >
            Login with mobile number
          </Box>
        </Box>
      )}

      {/* ── Step 1B: Mobile number ── */}
      {step === 'phone' && (
        <Box component="form" onSubmit={onPhoneSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Box>
            <Box component="label" htmlFor="lg-ph" sx={fieldLabel}>Mobile number</Box>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="span"
                sx={{
                  position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                  pr: '10px', borderRight: '1px solid var(--tl-line)',
                  fontFamily: UI, fontSize: 15, fontWeight: 700, color: 'var(--tl-g9)', pointerEvents: 'none',
                }}
              >
                +91
              </Box>
              <Box
                component="input"
                id="lg-ph"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="98765 43210"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
                autoFocus
                disabled={sending}
                sx={{ ...input, ...num, pl: '72px' }}
              />
            </Box>
          </Box>

          <Box component="button" type="submit" disabled={sending} sx={{ ...btnLg, width: '100%' }}>
            {sending && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
            {sending ? 'Sending…' : 'Send OTP'}
          </Box>

          <Box
            component="button"
            type="button"
            disabled={sending}
            onClick={() => { setStep('credentials'); setErrMsg(''); }}
            sx={{ ...textbtn, alignSelf: 'center' }}
          >
            Use Client ID / UHID instead
          </Box>
        </Box>
      )}

      {/* ── Step 2: OTP (shared by both modes) ── */}
      {step === 'otp' && (() => {
        const otpBusy =
          mode === 'credentials' ? signingInOtp :
          mode === 'doctor'      ? verifyingDoctor :
          verifying;
        return (
          <Box component="form" onSubmit={onOtpSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <OtpInput value={otp} onChange={setOtp} disabled={otpBusy} autoFocus />

            <Box component="button" type="submit" disabled={otpBusy} sx={{ ...btnLg, width: '100%' }}>
              {otpBusy && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
              {otpBusy ? 'Verifying…' : 'Verify & sign in'}
            </Box>

            <Box
              component="button"
              type="button"
              disabled={otpBusy}
              onClick={() => {
                setStep(mode); // back to whichever step launched the OTP
                setOtp('');
                setErrMsg('');
              }}
              sx={{ ...textbtn, alignSelf: 'center' }}
            >
              {mode === 'phone' ? 'Use a different number' : 'Re-enter password'}
            </Box>
          </Box>
        );
      })()}

      <Typography sx={{ fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)', textAlign: 'center', mt: '26px' }}>
        New to TrustLab?{' '}
        <Box component={Link} to={ROUTES.SIGNUP} sx={{ ...lnk, display: 'inline', minHeight: 0, fontSize: 14.5 }}>
          Sign up
        </Box>
      </Typography>

      {/* Forced change-password modal lives on the portal pages
          now (see MedicalDashboard / LabDashboard). After a
          successful credentials login we navigate the user to
          their portal and the modal pops THERE if their password
          is still the default — keeps the dialog and the dashboard
          they're about to use on the same screen. */}
      <RoleSelectModal
        open={roleModalOpen}
        clientId={clientId.trim()}
        centreName={centreName}
        onContinueAsClient={onContinueAsClient}
        onDoctorOtpSent={onDoctorOtpSent}
        onClose={() => setRoleModalOpen(false)}
      />
    </AuthShell>
  );
};

export default Login;
